import { FormEvent, useEffect, useState } from "react";
import { LoaderCircle, X } from "lucide-react";

import { createBooking, getBooking, getCatalog, getClient, getClients, updateBooking } from "./api";
import { BookingConversionForm } from "./BookingConversionForm";
import type {
  BookingRequestDetail,
  BookingRequestInput,
  Catalog,
  Client,
  ClientDetail,
} from "./types";

export function BookingPanel({
  bookingId,
  canWrite,
  onClose,
  onChanged,
}: {
  bookingId?: string;
  canWrite: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [booking, setBooking] = useState<BookingRequestDetail>();
  const [catalog, setCatalog] = useState<Catalog>({ services: [], addons: [] });
  const [clients, setClients] = useState<Client[]>([]);
  const [client, setClient] = useState<ClientDetail>();
  const [propertyId, setPropertyId] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    void Promise.all([
      getCatalog(),
      getClients({ limit: 100 }),
      bookingId ? getBooking(bookingId) : Promise.resolve(undefined),
    ])
      .then(async ([nextCatalog, clientPage, detail]) => {
        const nextClient = detail?.clientId ? await getClient(detail.clientId) : undefined;
        if (!active) return;
        setCatalog(nextCatalog);
        setClients(clientPage.items);
        setBooking(detail);
        setClient(nextClient);
        setPropertyId(detail?.propertyId ?? "");
      })
      .catch(() => active && setFailed(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [bookingId]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setFailed(false);
    const fields = new FormData(event.currentTarget);
    const input: BookingRequestInput = {
      clientId: nullable(fields.get("clientId")),
      propertyId: nullable(fields.get("propertyId")),
      serviceTypeId: String(fields.get("serviceTypeId")),
      preferredDate: nullable(fields.get("preferredDate")),
      preferredWindowStart: nullable(fields.get("preferredWindowStart")),
      preferredWindowEnd: nullable(fields.get("preferredWindowEnd")),
      frequency: nullable(fields.get("frequency")),
      bedrooms: nullableNumber(fields.get("bedrooms")),
      bathrooms: nullableNumber(fields.get("bathrooms")),
      floors: nullableNumber(fields.get("floors")),
      squareFeet: nullableNumber(fields.get("squareFeet")),
      specialNotes: nullable(fields.get("specialNotes")),
      status: String(fields.get("status") || "PENDING")
        .trim()
        .toUpperCase(),
      estimatedDurationMinutes: nullableNumber(fields.get("estimatedDurationMinutes")),
      estimatedPriceCents: dollarsToCents(fields.get("estimatedPrice")),
      addons: catalog.addons
        .filter((addon) => fields.get(`addon-${addon.id}`) === "on")
        .map((addon) => ({ addonId: addon.id, quantity: 1 })),
    };
    try {
      await (bookingId ? updateBooking(bookingId, input) : createBooking(input));
      await onChanged();
    } catch {
      setFailed(true);
      setSending(false);
    }
  }

  const availableServices = catalog.services.filter(
    (service) => service.active || service.id === booking?.serviceTypeId,
  );
  const selectedAddons = new Set(booking?.addons.map((addon) => addon.addonId));
  return (
    <div className="client-drawer-backdrop" onMouseDown={onClose}>
      <section
        className="client-drawer booking-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={bookingId ? "Booking request details" : "New booking request"}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="drawer-header">
          <div>
            <span className="eyebrow lime">{bookingId ? "Request details" : "New request"}</span>
            <h2>
              {booking?.clientName ?? (bookingId ? "Booking request" : "Capture preferences")}
            </h2>
          </div>
          <button className="icon-button" aria-label="Close booking request" onClick={onClose}>
            <X />
          </button>
        </header>
        <div className="drawer-body">
          {loading ? (
            <div className="table-state">
              <LoaderCircle className="spin" /> Loading request…
            </div>
          ) : (
            <div className="booking-workflows">
              <form className="record-form booking-form" onSubmit={submit}>
                <p className="booking-form-note">
                  <strong>Request only.</strong> Saving does not reserve team availability or
                  confirm an appointment.
                </p>
                <div className="form-grid">
                  <label>
                    Client
                    <select
                      name="clientId"
                      defaultValue={booking?.clientId ?? ""}
                      onChange={(event) => {
                        setPropertyId("");
                        void selectClient(event.target.value, setClient);
                      }}
                    >
                      <option value="">Not linked yet</option>
                      {clients.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Property
                    <select
                      name="propertyId"
                      value={propertyId}
                      disabled={!client}
                      onChange={(event) => setPropertyId(event.target.value)}
                    >
                      <option value="">Not linked yet</option>
                      {client?.properties.map((property) => (
                        <option key={property.id} value={property.id}>
                          {property.address}, {property.city}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="wide-field">
                    Service
                    <select
                      name="serviceTypeId"
                      defaultValue={booking?.serviceTypeId ?? ""}
                      required
                    >
                      <option value="" disabled>
                        Select a configured service
                      </option>
                      {availableServices.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Preferred date
                    <input
                      name="preferredDate"
                      type="date"
                      defaultValue={booking?.preferredDate ?? ""}
                    />
                  </label>
                  <label>
                    Frequency or cadence
                    <input
                      name="frequency"
                      defaultValue={booking?.frequency ?? ""}
                      maxLength={80}
                      placeholder="Enter only when known"
                    />
                  </label>
                  <label>
                    Window start
                    <input
                      name="preferredWindowStart"
                      type="time"
                      defaultValue={booking?.preferredWindowStart?.slice(0, 5) ?? ""}
                    />
                  </label>
                  <label>
                    Window end
                    <input
                      name="preferredWindowEnd"
                      type="time"
                      defaultValue={booking?.preferredWindowEnd?.slice(0, 5) ?? ""}
                    />
                  </label>
                  <NumberField
                    name="bedrooms"
                    label="Bedrooms"
                    value={booking?.bedrooms}
                    max={100}
                  />
                  <NumberField
                    name="bathrooms"
                    label="Bathrooms"
                    value={booking?.bathrooms}
                    max={100}
                    step="0.5"
                  />
                  <NumberField name="floors" label="Floors" value={booking?.floors} max={100} />
                  <NumberField
                    name="squareFeet"
                    label="Square feet"
                    value={booking?.squareFeet}
                    max={10_000_000}
                  />
                  {catalog.addons.length ? (
                    <fieldset className="wide-field addon-fieldset">
                      <legend>Add-ons</legend>
                      <div>
                        {catalog.addons
                          .filter((addon) => addon.active || selectedAddons.has(addon.id))
                          .map((addon) => (
                            <label key={addon.id}>
                              <input
                                name={`addon-${addon.id}`}
                                type="checkbox"
                                defaultChecked={selectedAddons.has(addon.id)}
                              />{" "}
                              {addon.name}
                            </label>
                          ))}
                      </div>
                    </fieldset>
                  ) : null}
                  <label className="wide-field">
                    Special notes
                    <textarea
                      name="specialNotes"
                      rows={4}
                      maxLength={10000}
                      defaultValue={booking?.specialNotes ?? ""}
                    />
                  </label>
                  {bookingId ? (
                    <label>
                      Workflow status
                      <input
                        name="status"
                        defaultValue={booking?.status ?? "PENDING"}
                        pattern="[A-Za-z][A-Za-z0-9_]*"
                        maxLength={40}
                        required
                      />
                    </label>
                  ) : (
                    <input name="status" type="hidden" value="PENDING" />
                  )}
                  <NumberField
                    name="estimatedDurationMinutes"
                    label="Estimated duration (minutes)"
                    value={booking?.estimatedDurationMinutes}
                    max={10_080}
                  />
                  <label>
                    Estimated price (USD)
                    <input
                      name="estimatedPrice"
                      type="number"
                      min="0"
                      step="0.01"
                      defaultValue={
                        booking?.estimatedPriceCents != null
                          ? (booking.estimatedPriceCents / 100).toFixed(2)
                          : ""
                      }
                    />
                  </label>
                </div>
                {failed ? (
                  <p className="form-error" role="alert">
                    We couldn’t save this request. Check the selected records and time window, then
                    try again.
                  </p>
                ) : null}
                {!canWrite ? <p className="form-error">Your role has read-only access.</p> : null}
                <div className="form-actions">
                  <button type="button" className="secondary-button" onClick={onClose}>
                    Cancel
                  </button>
                  <button
                    className="primary-button compact-action"
                    disabled={sending || !canWrite || availableServices.length === 0}
                  >
                    {sending ? <LoaderCircle className="spin" /> : null} Save request
                  </button>
                </div>
              </form>
              {bookingId && booking ? (
                <BookingConversionForm
                  booking={booking}
                  canWrite={canWrite}
                  onConverted={onChanged}
                />
              ) : null}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function NumberField({
  name,
  label,
  value,
  max,
  step,
}: {
  name: string;
  label: string;
  value?: number | null;
  max: number;
  step?: string;
}) {
  return (
    <label>
      {label}
      <input name={name} type="number" min="0" max={max} step={step} defaultValue={value ?? ""} />
    </label>
  );
}
async function selectClient(id: string, setClient: (value: ClientDetail | undefined) => void) {
  setClient(id ? await getClient(id) : undefined);
}
function nullable(value: FormDataEntryValue | null) {
  const text = String(value ?? "").trim();
  return text || null;
}
function nullableNumber(value: FormDataEntryValue | null) {
  const text = String(value ?? "").trim();
  return text ? Number(text) : null;
}
function dollarsToCents(value: FormDataEntryValue | null) {
  const text = String(value ?? "").trim();
  return text ? Math.round(Number(text) * 100) : null;
}
